import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listAnalyses } from "@/lib/api";
import { Plus, MagnifyingGlass, Sparkle } from "@phosphor-icons/react";

function statusDot(status) {
  const map = {
    queued: "bg-zinc-400",
    crawling: "bg-amber-500 animate-pulse",
    fingerprinting: "bg-amber-500 animate-pulse",
    business_profile: "bg-amber-500 animate-pulse",
    feature_inventory: "bg-amber-500 animate-pulse",
    industry_lens: "bg-amber-500 animate-pulse",
    build_spec: "bg-amber-500 animate-pulse",
    report: "bg-amber-500 animate-pulse",
    complete: "bg-emerald-600",
    failed: "bg-red-600",
  };
  return map[status] || "bg-zinc-400";
}

function shortUrl(u) {
  try {
    const url = new URL(u);
    return url.host + (url.pathname !== "/" ? url.pathname : "");
  } catch {
    return u;
  }
}

export default function Sidebar() {
  const location = useLocation();
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["analyses"],
    queryFn: listAnalyses,
    refetchInterval: 5000,
  });

  return (
    <aside
      data-testid="sidebar"
      className="w-72 border-r border-zinc-200 bg-zinc-50 h-screen sticky top-0 flex flex-col"
    >
      <div className="px-5 py-5 border-b border-zinc-200">
        <Link to="/" className="block" data-testid="sidebar-brand">
          <div className="font-display font-bold text-lg tracking-tight text-zinc-950">
            Teardown<span className="text-emerald-600">IQ</span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">
            Phase 1 · Build Intelligence
          </div>
        </Link>
      </div>

      <Link
        to="/"
        data-testid="new-analysis-link"
        className="mx-4 mt-4 mb-2 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white py-2.5 px-3 font-mono text-xs uppercase tracking-wider hover:bg-zinc-800 transition-colors"
      >
        <Plus size={14} weight="bold" />
        New analysis
      </Link>

      <Link
        to="/lenses"
        data-testid="lenses-link"
        className="mx-4 mb-2 inline-flex items-center justify-center gap-2 border border-zinc-300 text-zinc-800 py-2 px-3 font-mono text-xs uppercase tracking-wider hover:bg-white transition-colors"
      >
        Industry Lenses
      </Link>

      <Link
        to="/radar"
        data-testid="radar-link"
        className="mx-4 mb-2 inline-flex items-center justify-center gap-2 border border-zinc-300 text-zinc-800 py-2 px-3 font-mono text-xs uppercase tracking-wider hover:bg-white transition-colors"
      >
        Public SaaS Radar
      </Link>

      <Link
        to="/studio"
        data-testid="iq-studio-link"
        className="mx-4 mb-2 inline-flex items-center justify-between gap-2 bg-emerald-600 text-white py-2 px-3 font-mono text-xs uppercase tracking-wider hover:bg-emerald-700 transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          <Sparkle size={14} weight="fill" /> IQ Studio
        </span>
        <span className="text-[9px] bg-white/20 px-1.5 py-0.5 tracking-widest">PRO</span>
      </Link>

      <div className="px-5 pt-4 pb-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
        <MagnifyingGlass size={12} /> History
        <span className="ml-auto">{items.length}</span>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-4 space-y-1" data-testid="analysis-history">
        {isLoading && (
          <div className="px-3 py-2 text-xs text-zinc-500 font-mono">Loading…</div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="px-3 py-4 text-xs text-zinc-500">
            No analyses yet. Paste a SaaS URL to get started.
          </div>
        )}
        {items.map((it) => {
          const active = location.pathname.includes(it.id);
          return (
            <Link
              key={it.id}
              to={`/analysis/${it.id}`}
              data-testid={`history-item-${it.id}`}
              className={`block px-3 py-2 border ${
                active
                  ? "border-zinc-950 bg-white"
                  : "border-transparent hover:border-zinc-300 hover:bg-white"
              } transition-colors`}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 ${statusDot(it.status)}`} />
                <span className="font-mono text-[11px] text-zinc-900 truncate">
                  {shortUrl(it.target_url)}
                </span>
              </div>
              {it.vertical && (
                <div className="mt-1 text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                  {it.vertical}
                </div>
              )}
              {it.one_line_pitch && (
                <div className="mt-1 text-xs text-zinc-600 line-clamp-2">
                  {it.one_line_pitch}
                </div>
              )}
            </Link>
          );
        })}
      </div>

      <div className="px-5 py-3 border-t border-zinc-200 text-[10px] font-mono text-zinc-500 leading-relaxed">
        Public-surface analysis only. We respect robots.txt and rate-limit politely. No auth bypass.
      </div>
    </aside>
  );
}
